import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, Edit2, Save, X, Plus, Image, Video } from 'lucide-react';
import { getGalleriesByCategory, createGallery, updateGallery, deleteGallery, uploadFile } from '../../services/api';

const GalleryManager = () => {
  const [activeCategory, setActiveCategory] = useState('main');
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'image',
    url: '',
    youtube_url: '',
    title: '',
    caption: '',
    order: 0
  });
  const [uploading, setUploading] = useState(false);

  const categories = [
    { id: 'main', name: 'Main Gallery', desc: 'Explore our visual journey and moments' },
    { id: 'art', name: 'Art Gallery', desc: 'Discover psychedelic artwork and visuals' },
    { id: 'party', name: 'Party Gallery', desc: 'Experience our events and party vibes' }
  ];

  const fetchGalleries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getGalleriesByCategory(activeCategory);
      setGalleries(response.data);
    } catch (error) {
      console.error('Error fetching galleries:', error);
    }
    setLoading(false);
  }, [activeCategory]);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFile(file);
      setFormData({ ...formData, url: response.data.url });
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file');
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        category: activeCategory
      };

      if (editingId) {
        await updateGallery(editingId, data);
      } else {
        await createGallery(data);
      }

      fetchGalleries();
      resetForm();
    } catch (error) {
      console.error('Error saving gallery item:', error);
      alert('Failed to save gallery item');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await deleteGallery(id);
        fetchGalleries();
      } catch (error) {
        console.error('Error deleting gallery item:', error);
      }
    }
  };

  const handleEdit = (item) => {
    setFormData({
      type: item.type,
      url: item.url || '',
      youtube_url: item.youtube_url || '',
      title: item.title || '',
      caption: item.caption || '',
      order: item.order
    });
    setEditingId(item.id);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      type: 'image',
      url: '',
      youtube_url: '',
      title: '',
      caption: '',
      order: 0
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-orange-500">Gallery Manager</h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-orange-500 text-black px-4 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2"
        >
          {showAddForm ? <X size={20} /> : <Plus size={20} />}
          {showAddForm ? 'Cancel' : 'Add Item'}
        </button>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => {
              setActiveCategory(cat.id);
              resetForm();
            }}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeCategory === cat.id
                ? 'text-orange-500 border-b-2 border-orange-500'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-gray-800 p-6 rounded-lg border border-orange-500/30">
          <h3 className="text-xl font-bold text-orange-500 mb-4">
            {editingId ? 'Edit Gallery Item' : 'Add New Gallery Item'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Type</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="image"
                    checked={formData.type === 'image'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="text-orange-500"
                  />
                  <Image size={20} />
                  <span>Image</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="video"
                    checked={formData.type === 'video'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="text-orange-500"
                  />
                  <Video size={20} />
                  <span>Video</span>
                </label>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Upload {formData.type === 'image' ? 'Image' : 'Video'}
              </label>
              <div className="flex gap-2">
                <input
                  type="file"
                  accept={formData.type === 'image' ? 'image/*' : 'video/*'}
                  onChange={handleFileUpload}
                  className="flex-1 bg-gray-700 text-white p-2 rounded border border-gray-600"
                />
                {uploading && <span className="text-orange-500">Uploading...</span>}
              </div>
              {formData.url && (
                <div className="mt-2 text-sm text-green-500">✓ File uploaded successfully</div>
              )}
            </div>

            {/* YouTube URL (for videos) */}
            {formData.type === 'video' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  YouTube URL (Optional - use this OR upload video)
                </label>
                <input
                  type="url"
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600"
                />
              </div>
            )}

            {/* Title (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Title (Optional)
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter title..."
                className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600"
              />
            </div>

            {/* Caption (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Caption (Optional)
              </label>
              <textarea
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                placeholder="Enter caption..."
                rows="3"
                className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600"
              />
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Display Order
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full bg-gray-700 text-white p-2 rounded border border-gray-600"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-orange-500 text-black px-6 py-2 rounded-lg hover:bg-orange-600 flex items-center gap-2"
              >
                <Save size={20} />
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Gallery Items List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center text-gray-400 py-8">Loading...</div>
        ) : galleries.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            No items in this gallery. Click "Add Item" to get started.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleries.map((item) => (
              <div key={item.id} className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                {/* Media Preview */}
                <div className="relative h-48 bg-gray-900">
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.title || 'Gallery item'}
                      className="w-full h-full object-cover"
                    />
                  ) : item.youtube_url ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <Video size={48} className="text-orange-500" />
                      <span className="ml-2 text-sm text-gray-400">YouTube Video</span>
                    </div>
                  ) : (
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                      controls
                    />
                  )}
                </div>

                {/* Item Info */}
                <div className="p-4 space-y-2">
                  {item.title && (
                    <h4 className="font-semibold text-orange-500">{item.title}</h4>
                  )}
                  {item.caption && (
                    <p className="text-sm text-gray-400">{item.caption}</p>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                    <span className="text-xs text-gray-500">Order: {item.order}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryManager;
